import {
  parseHostSource,
  type HostSourceData,
  type HostSourceRequest,
} from "@proof/core";

/**
 * What a host returns for a source request: the data itself, or the data plus
 * a translation function that maps the host's own structure into the expected
 * shape. Translation always runs before the data is used.
 */
export interface SourceInputResult {
  data: unknown;
  translate?: (
    data: unknown,
    request: HostSourceRequest,
  ) => unknown | Promise<unknown>;
}
/** Host callback for source inputs (alternative to the DOM event). */
export type SourceInputHandler = (
  request: HostSourceRequest,
  signal: AbortSignal,
) => SourceInputResult | Promise<SourceInputResult>;
/**
 * Detail of the `proof:source-request` event. A host listener calls
 * `respondWith` synchronously with the result (or a promise of it), like a
 * service worker FetchEvent. `signal` aborts when the request is cancelled.
 */
export interface SourceRequestEventDetail {
  request: HostSourceRequest;
  signal: AbortSignal;
  respondWith(result: SourceInputResult | Promise<SourceInputResult>): void;
}
export const sourceRequestEvent = "proof:source-request";

function abortable<T>(promise: Promise<T>, signal: AbortSignal) {
  return new Promise<T>((resolve, reject) => {
    if (signal.aborted) return reject(signal.reason);
    const abort = () => reject(signal.reason);
    signal.addEventListener("abort", abort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", abort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", abort);
        reject(error);
      },
    );
  });
}
/**
 * Requests one source from the parent page through the host callback, or else
 * the standard `proof:source-request` event, then translates and validates it.
 */
export async function requestHostSource(
  target: EventTarget,
  request: HostSourceRequest,
  handler: SourceInputHandler | undefined,
  signal: AbortSignal,
  timeoutMs = 30000,
): Promise<HostSourceData> {
  const timeout = AbortSignal.timeout(timeoutMs);
  const combined = AbortSignal.any([signal, timeout]);
  let pending: Promise<SourceInputResult> | undefined;
  if (handler) pending = Promise.resolve(handler(request, combined));
  else {
    let answered = false;
    const detail: SourceRequestEventDetail = {
      request: structuredClone(request),
      signal: combined,
      respondWith(result) {
        if (answered)
          throw new Error("respondWith may only be called once per request");
        answered = true;
        pending = Promise.resolve(result);
      },
    };
    target.dispatchEvent(
      new CustomEvent(sourceRequestEvent, {
        detail,
        bubbles: true,
        composed: true,
        cancelable: true,
      }),
    );
    if (!pending)
      throw new Error(
        `${request.label}: no host source provider answered "${request.key}". Listen for ${sourceRequestEvent} or set sourceInputHandler.`,
      );
  }
  let result: SourceInputResult;
  try {
    result = await abortable(pending!, combined);
  } catch (error) {
    if (timeout.aborted && !signal.aborted)
      throw new Error(`${request.label}: the host did not respond in time.`);
    throw error;
  }
  if (!result || typeof result !== "object" || !("data" in result))
    throw new Error(
      `${request.label}: the host must return { data } (optionally with translate).`,
    );
  const translated =
    typeof result.translate === "function"
      ? await abortable(
          Promise.resolve(result.translate(result.data, request)),
          combined,
        )
      : result.data;
  return parseHostSource(request, translated);
}
